<template>
  <div class="credit-model-details">
    <!-- Header Section -->
    <div class="assessment-header">
      <div class="header-content">
        <div class="header-text">
          <h2 class="assessment-title">Credit Assessment Analysis</h2>
        </div>
      </div>
    </div>

    <!-- Summary Section -->
    <div v-if="summaryCategoryItems.length > 0 || summaryGrandTotalItems.length > 0" class="summary-section">
      <div class="summary-section-header">
        <h3 class="section-title">Credit Model</h3>
      </div>
      <div class="summary-layout">
        <div v-if="summaryCategoryItems.length > 0" class="summary-category-column">
          <div class="summary-category-list">
            <div class="summary-category-header">
              <span>Risk Category</span>
              <span>Score</span>
            </div>
            <div
              v-for="(item, index) in summaryCategoryItems"
              :key="`summary-category-${index}`"
              class="summary-category-row"
            >
              <span class="summary-risk-name">{{ item.Risk }}</span>
              <span class="summary-score">
                <strong>{{ formatNumber(item['Obtained Score']) }}</strong>
                <span>/ {{ formatNumber(item['Total Score']) }}</span>
              </span>
            </div>
          </div>

          <div v-if="orgDetailsLoaded" class="org-details-metrics">
            <div class="approved-rate-display">
              <span class="approved-rate-label">Approved Rate</span>
              <strong class="approved-rate-value">{{ formatApprovedRate(approvedRate) }}</strong>
            </div>
            <div class="approved-rate-display">
              <span class="approved-rate-label">Allowed Credit Limit</span>
              <strong class="approved-rate-value">{{ formatAllowedCreditLimit(allowedCreditLimit) }}</strong>
            </div>
            <div class="approved-rate-display">
              <span class="approved-rate-label">Monthly Credit Commitment</span>
              <strong class="approved-rate-value">{{ formatAllowedCreditLimit(kemaDebtEmi) }}</strong>
            </div>
          </div>
        </div>

        <div v-if="summaryGrandTotalItems.length > 0" class="summary-final-list">
          <div
            v-for="(item, index) in summaryGrandTotalItems"
            :key="`summary-grand-total-${index}`"
            class="summary-final-card"
          >
            <div class="summary-final-label">{{ item.Risk }}</div>
            <div
              class="risk-gauge"
              role="img"
              :aria-label="`${getRiskRatingLabel(item['Obtained Score'], item['Total Score'])}: ${formatScorePercentage(item['% Score'])}`"
            >
              <div class="risk-gauge-dial">
                <div class="risk-gauge-arc"></div>
                <div class="risk-gauge-mask"></div>
                <div
                  class="risk-gauge-needle"
                  :style="{ transform: `rotate(${getRiskGaugeAngle(item['Obtained Score'], item['Total Score'])}deg)` }"
                ></div>
                <div class="risk-gauge-hub"></div>
              </div>
              <div class="risk-gauge-scale">
                <span>0</span>
                <span>100</span>
              </div>
              <div class="risk-gauge-legend">
                <span><i class="gauge-key gauge-key-unacceptable"></i>Unacceptable</span>
                <span><i class="gauge-key gauge-key-high"></i>High Risk</span>
                <span><i class="gauge-key gauge-key-moderate"></i>Moderate Risk</span>
                <span><i class="gauge-key gauge-key-moderately-low"></i>Moderately Low Risk</span>
                <span><i class="gauge-key gauge-key-low"></i>Low Risk</span>
                <span><i class="gauge-key gauge-key-very-low"></i>Very Low Risk</span>
              </div>
            </div>
            <div class="summary-final-main">
              <div class="summary-final-score">
                <strong
                  :class="getRiskRatingClass(item['Obtained Score'], item['Total Score'])"
                >{{ formatNumber(item['Obtained Score']) }}</strong>
                <span>/ {{ formatNumber(item['Total Score']) }}</span>
              </div>
              <span
                class="risk-rating-badge"
                :class="getRiskRatingClass(item['Obtained Score'], item['Total Score'])"
              >{{ getRiskRatingLabel(item['Obtained Score'], item['Total Score']) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="relationshipCustomers.length > 0" class="approved-payers-section">
      <h3 class="section-title">Kema Approved Payers</h3>
      <ol v-if="kemaApprovedPayers.length > 0" class="approved-payers-list">
        <li
          v-for="(name, index) in kemaApprovedPayers"
          :key="`kema-approved-payer-${index}`"
          class="approved-payers-item"
        >
          <span class="approved-payers-index">{{ index + 1 }}.</span>
          <span class="approved-payers-name">{{ name }}</span>
        </li>
      </ol>
      <p v-else class="approved-payers-empty">No Kema approved payers found.</p>
    </div>

    <div class="risk-sections-grid">
    <!-- Operations Section -->
    <div v-if="riskRatingData.Operations && riskRatingData.Operations.length > 0" class="risk-section">
      <h3 class="section-title">
        Operations Risk
      </h3>
      <div class="metrics-table-container">
        <table class="metrics-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Current Value</th>
              <th>Obtained Score</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="(item, index) in riskRatingData.Operations" 
              :key="index"
              :class="{ 'risk-total-row': item.Operations && item.Operations.includes('Risk') }"
            >
              <td class="metric-name">{{ formatMetricName(item.Operations) }}</td>
              <td class="current-value">{{ formatValue(item['Current Value']) }}</td>
              <td class="obtained-score-cell">
                <div class="score-container">
                  <span class="obtained-score score-ratio" :class="getScoreClass(item['Obtained Score'], item.Weights)">
                    {{ formatNumber(item['Obtained Score']) }} / {{ formatNumber(item.Weights) }}
                  </span>
                  <div class="score-progress-mini">
                    <div 
                      class="progress-mini-fill" 
                      :class="getScoreClass(item['Obtained Score'], item.Weights)"
                      :style="{ width: `${getScoreProgressWidth(item['Obtained Score'], item.Weights)}%` }"
                    ></div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Management, Business & Industry Section -->
    <div v-if="riskRatingData['Mgmt, Bus & Ind.'] && riskRatingData['Mgmt, Bus & Ind.'].length > 0" class="risk-section">
      <h3 class="section-title">
        Management, Business & Industry Risk
      </h3>
      <div class="metrics-table-container">
        <table class="metrics-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Current Value</th>
              <th>Obtained Score</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="(item, index) in riskRatingData['Mgmt, Bus & Ind.']" 
              :key="index"
              :class="{ 'risk-total-row': item['Mgmt, Bus & Ind.'] && item['Mgmt, Bus & Ind.'].includes('Risk') }"
            >
              <td class="metric-name">{{ formatMetricName(item['Mgmt, Bus & Ind.']) }}</td>
              <td class="current-value">{{ formatValue(item['Current Value']) }}</td>
              <td class="obtained-score-cell">
                <div class="score-container">
                  <span class="obtained-score score-ratio" :class="getScoreClass(item['Obtained Score'], item.Weights)">
                    {{ formatNumber(item['Obtained Score']) }} / {{ formatNumber(item.Weights) }}
                  </span>
                  <div class="score-progress-mini">
                    <div 
                      class="progress-mini-fill" 
                      :class="getScoreClass(item['Obtained Score'], item.Weights)"
                      :style="{ width: `${getScoreProgressWidth(item['Obtained Score'], item.Weights)}%` }"
                    ></div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Liquidity Section -->
    <div v-if="riskRatingData.Liquidity && riskRatingData.Liquidity.length > 0" class="risk-section">
      <h3 class="section-title">
        Liquidity Risk
      </h3>
      <div class="metrics-table-container">
        <table class="metrics-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Current Value</th>
              <th>Obtained Score</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="(item, index) in riskRatingData.Liquidity" 
              :key="index"
              :class="{ 'risk-total-row': (item.Liquidity || item['Liquidity(No AECB)']) && (item.Liquidity || item['Liquidity(No AECB)'].includes('Risk')) }"
            >
              <td class="metric-name">{{ formatMetricName(item.Liquidity || item['Liquidity(No AECB)']) }}</td>
              <td class="current-value">{{ formatValue(item['Current Value']) }}</td>
              <td class="obtained-score-cell">
                <div class="score-container">
                  <span class="obtained-score score-ratio" :class="getScoreClass(item['Obtained Score'], item.Weights)">
                    {{ formatNumber(item['Obtained Score']) }} / {{ formatNumber(item.Weights) }}
                  </span>
                  <div class="score-progress-mini">
                    <div 
                      class="progress-mini-fill" 
                      :class="getScoreClass(item['Obtained Score'], item.Weights)"
                      :style="{ width: `${getScoreProgressWidth(item['Obtained Score'], item.Weights)}%` }"
                    ></div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Third Party Section -->
    <div v-if="riskRatingData['Third Party'] && riskRatingData['Third Party'].length > 0" class="risk-section">
      <h3 class="section-title">
        Third Party Risk
      </h3>
      <div class="metrics-table-container">
        <table class="metrics-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Current Value</th>
              <th>Obtained Score</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="(item, index) in riskRatingData['Third Party']" 
              :key="index"
              :class="{ 'risk-total-row': (item['Third Party'] || item['Third Party(No AECB)']) && (item['Third Party'] || item['Third Party(No AECB)'].includes('Risk')) }"
            >
              <td class="metric-name">{{ formatMetricName(item['Third Party'] || item['Third Party(No AECB)']) }}</td>
              <td class="current-value">{{ formatValue(item['Current Value']) }}</td>
              <td class="obtained-score-cell">
                <div class="score-container">
                  <span class="obtained-score score-ratio" :class="getScoreClass(item['Obtained Score'], item.Weights)">
                    {{ formatNumber(item['Obtained Score']) }} / {{ formatNumber(item.Weights) }}
                  </span>
                  <div class="score-progress-mini">
                    <div 
                      class="progress-mini-fill" 
                      :class="getScoreClass(item['Obtained Score'], item.Weights)"
                      :style="{ width: `${getScoreProgressWidth(item['Obtained Score'], item.Weights)}%` }"
                    ></div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    </div>

    <!-- No Data Message -->
    <div v-if="!hasData" class="no-data">
      <div class="no-data-icon">
        <i class="las la-file-alt"></i>
      </div>
      <h3>No Credit Model Data</h3>
      <p>No credit model data is available for this file.</p>
    </div>
  </div>
</template>

<script>
import ChatbotService from '@/services/chatbot.service';

export default {
  name: 'CreditModelDetails',
  props: {
    data: {
      type: Object,
      required: true
    },
    customersRelationshipData: {
      type: Array,
      default: null
    }
  },
  data() {
    return {
      approvedRate: null,
      allowedCreditLimit: null,
      kemaDebtEmi: null,
      orgDetailsLoaded: false
    };
  },
  async mounted() {
    await this.loadOrganisationDetails();
  },
  computed: {
    candidateId() {
      return this.$route?.params?.candidateId || null;
    },
    riskRatingData() {
      return this.data?.risk_rating_data || this.data || {};
    },
    relationshipCustomers() {
      if (Array.isArray(this.customersRelationshipData)) {
        return this.customersRelationshipData;
      }

      if (Array.isArray(this.data?.customers_relationship_data)) {
        return this.data.customers_relationship_data;
      }

      return [];
    },
    kemaApprovedPayers() {
      return this.relationshipCustomers
        .filter((customer) => this.isKemaApproved(customer))
        .map((customer) => customer.BusinessName)
        .filter(Boolean);
    },
    summaryItems() {
      const summary = this.riskRatingData.Summary;
      if (!Array.isArray(summary)) return [];
      return summary.filter((item) => this.isValidSummaryItem(item));
    },
    summaryCategoryItems() {
      return this.summaryItems.filter((item) => !this.isGrandTotalItem(item));
    },
    summaryGrandTotalItems() {
      const grandTotals = this.summaryItems.filter((item) => this.isGrandTotalItem(item));
      if (grandTotals.length <= 1) return grandTotals;

      const recalculated = grandTotals.filter((item) =>
        /recalculated\s*wcr/i.test(String(item.Risk))
      );

      return recalculated.length > 0 ? recalculated : grandTotals;
    },
    hasData() {
      const data = this.riskRatingData;
      return (data && (
        (data.Operations && data.Operations.length > 0) ||
        (data['Mgmt, Bus & Ind.'] && data['Mgmt, Bus & Ind.'].length > 0) ||
        (data.Liquidity && data.Liquidity.length > 0) ||
        (data['Third Party'] && data['Third Party'].length > 0) ||
        this.summaryItems.length > 0
      )) || this.relationshipCustomers.length > 0;
    }
  },
  methods: {
    async loadOrganisationDetails() {
      if (!this.candidateId) {
        this.orgDetailsLoaded = true;
        return;
      }

      try {
        const details = await ChatbotService.getOrganisationDetails(this.candidateId);
        const rate = details?.approved_rate ?? details?.approvedRate;
        this.approvedRate = rate === '' || rate === null || rate === undefined ? null : rate;

        const limit = details?.final_credit_limit;
        this.allowedCreditLimit = limit === '' || limit === null || limit === undefined ? null : limit;

        const emi = details?.kema_debt_emi ?? details?.kemaDebtEmi;
        this.kemaDebtEmi = emi === '' || emi === null || emi === undefined ? null : emi;
      } catch (error) {
        console.error('Error loading organisation details:', error);
      } finally {
        this.orgDetailsLoaded = true;
      }
    },
    formatApprovedRate(value) {
      if (value === null || value === undefined || value === '') {
        return 'N/A';
      }

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        return String(value);
      }

      return `${numericValue}%`;
    },
    formatAllowedCreditLimit(value) {
      if (value === null || value === undefined || value === '') {
        return 'N/A';
      }

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        return String(value);
      }

      return new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: 'AED',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numericValue);
    },
    isValidSummaryItem(item) {
      return item && item.Risk !== undefined && item.Risk !== null && item.Risk !== '';
    },
    isGrandTotalItem(item) {
      return this.isValidSummaryItem(item) && String(item.Risk).includes('Grand Total');
    },
    isKemaApproved(customer) {
      const status = customer?.['Kema Approved Status'];
      if (!status) return false;

      return String(status).trim().toLowerCase() === 'approved';
    },
    formatMetricName(value) {
      if (value === null || value === undefined || value === '') return '';
      if (typeof value === 'string') {
        return value.replace(/_/g, ' ');;
      }
    },
    formatValue(value) {
      if (value === null || value === undefined || value === '') return '';
      if (typeof value === 'string') return value;
      if (typeof value === 'number') {
        // Format large numbers with commas
        return value.toLocaleString('en-US', {
          minimumFractionDigits: value % 1 !== 0 ? 2 : 0,
          maximumFractionDigits: 2
        });
      }
      return value;
    },
    
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return 'N/A';
      if (typeof value === 'number') {
        return value.toLocaleString('en-US', {
          minimumFractionDigits: value % 1 !== 0 ? 1 : 0,
          maximumFractionDigits: 1
        });
      }
      return value;
    },
    
    formatPercentage(value) {
      if (value === null || value === undefined || value === '') return 'N/A';
      if (typeof value === 'number') {
        return `${(value * 100).toFixed(1)}%`;
      }
      return value;
    },
    
    getScoreClass(obtainedScore, totalScore) {
      if (obtainedScore === null || obtainedScore === undefined || totalScore === null || totalScore === undefined) {
        return '';
      }
      
      const percentage = totalScore > 0 ? obtainedScore / totalScore : 0;
      
      if (percentage >= 0.8) return 'score-excellent';
      if (percentage >= 0.6) return 'score-good';
      if (percentage >= 0.4) return 'score-fair';
      return 'score-poor';
    },

    getScoreProgressWidth(obtainedScore, totalScore) {
      const obtained = Number(obtainedScore);
      const total = Number(totalScore);

      if (!Number.isFinite(obtained) || !Number.isFinite(total) || total <= 0) {
        return 0;
      }

      return Math.min(100, Math.max(0, (obtained / total) * 100));
    },
    
    getScoreClassFromPercentage(percentage) {
      if (percentage === null || percentage === undefined || percentage === '') {
        return 'score-poor';
      }

      const percentValue = this.getProgressWidth(percentage);

      if (!Number.isFinite(percentValue)) {
        return 'score-poor';
      }

      if (percentValue >= 80) return 'score-excellent';
      if (percentValue >= 60) return 'score-good';
      if (percentValue >= 40) return 'score-fair';
      return 'score-poor';
    },
    
    getProgressWidth(percentage) {
      if (percentage === null || percentage === undefined || percentage === '') {
        return 0;
      }
      
      const percentValue = typeof percentage === 'number' ? percentage : parseFloat(percentage);
      
      if (isNaN(percentValue)) {
        return 0;
      }
      
      // If percentage is already between 0-100, use it directly
      if (percentValue > 1) {
        return Math.min(100, Math.max(0, percentValue));
      }
      
      // If percentage is between 0-1, convert to 0-100
      return Math.min(100, Math.max(0, percentValue * 100));
    },

    formatScorePercentage(percentage) {
      const value = this.getProgressWidth(percentage);
      return `${Number.isInteger(value) ? value : value.toFixed(1)}%`;
    },

    getNormalizedRiskScore(obtainedScore, totalScore) {
      const obtained = Number(obtainedScore);
      const total = Number(totalScore);

      if (!Number.isFinite(obtained) || !Number.isFinite(total) || total <= 0) {
        return null;
      }

      return Math.min(100, Math.max(0, (obtained / total) * 100));
    },

    getRiskRatingLabel(obtainedScore, totalScore) {
      const score = this.getNormalizedRiskScore(obtainedScore, totalScore);

      if (score === null) return 'Not rated';
      if (score >= 90) return 'Very Low Risk';
      if (score >= 80) return 'Low Risk';
      if (score >= 75) return 'Moderately Low Risk';
      if (score >= 70) return 'Moderate Risk';
      if (score >= 50) return 'High Risk';
      return 'Unacceptable';
    },

    getRiskRatingClass(obtainedScore, totalScore) {
      const score = this.getNormalizedRiskScore(obtainedScore, totalScore);

      if (score === null) return 'risk-rating-unrated';
      if (score >= 90) return 'risk-rating-very-low';
      if (score >= 80) return 'risk-rating-low';
      if (score >= 75) return 'risk-rating-moderately-low';
      if (score >= 70) return 'risk-rating-moderate';
      if (score >= 50) return 'risk-rating-high';
      return 'risk-rating-unacceptable';
    },

    getRiskGaugeAngle(obtainedScore, totalScore) {
      const score = this.getNormalizedRiskScore(obtainedScore, totalScore);
      const normalized = score === null ? 0 : score;
      return 180 + (normalized * 1.8);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../index.scss';

.credit-model-details {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.assessment-header {
  background: transparent;
  border-radius: 0;
  padding: 0;
  color: inherit;
  box-shadow: none;
  
  .header-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    
    .header-icon {
      width: 48px;
      height: 48px;
      background: transparent;
      border-radius: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      
      i {
        font-size: 1.5rem;
        color: $color-primary;
      }
    }
    
    .header-text {
      margin-top: 1rem;
      flex: 1;
      
      .assessment-title {
        font-size: 1.5rem;
        font-weight: 700;
        margin: 0 0 0.25rem 0;
        font-family: $brand-font-medium;
        letter-spacing: -0.3px;
        color: #0f172a;
      }
      
      .assessment-subtitle {
        font-size: 0.875rem;
        margin: 0;
        color: #64748b;
        font-family: $brand-font-regular;
      }
    }
  }
}

.summary-section {
  padding: 1.5rem 0;
  margin: 0;

  .summary-section-header {
    margin-bottom: 0.75rem;
  }

  .section-title {
    margin: 0;
    color: #0f172a;
    font-family: $brand-font-medium;
    font-size: 1.125rem;
    font-weight: 700;
    line-height: 1.35;
    letter-spacing: -0.15px;
  }

  .summary-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 320px;
    gap: 1rem;
    align-items: stretch;
  }

  .summary-category-column {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .summary-category-list {
    display: flex;
    flex-direction: column;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #ffffff;
    overflow: hidden;
  }

  .summary-category-header,
  .summary-category-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 120px;
    gap: 1rem;
    align-items: center;
  }

  .summary-category-header {
    padding: 0.6rem 1rem;
    border-bottom: 1px solid #e2e8f0;
    color: #64748b;
    background: #f8fafc;
    font-family: $brand-font-medium;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;

    span:nth-child(2) {
      text-align: center;
    }
  }

  .summary-category-row {
    flex: 1;
    min-height: 58px;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #eef2f7;

    &:last-child {
      border-bottom: none;
    }
  }

  .org-details-metrics {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-left: 1rem;
  }

  .approved-rate-display {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.25rem 0;
  }

  .approved-rate-label {
    color: #64748b;
    font-family: $brand-font-medium;
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .approved-rate-value {
    padding-right: 2.5rem;
    color: #0f172a;
    font-family: $brand-font-medium;
    font-size: 1.1rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .summary-risk-name {
    color: #1e293b;
    font-family: $brand-font-medium;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.35;
  }

  .summary-score {
    font-family: $brand-font-medium;
    font-variant-numeric: tabular-nums;
    text-align: center;
    white-space: nowrap;

    strong {
      color: #0f172a;
      font-size: 1.1rem;
      font-weight: 700;
    }

    span {
      color: #64748b;
      font-size: 0.9rem;
    }
  }

  .summary-final-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .summary-final-card {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 100%;
    padding: 1rem;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background: #ffffff;
  }

  .summary-final-label {
    color: #475569;
    font-family: $brand-font-medium;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .risk-gauge {
    width: 240px;
    max-width: 100%;
    margin: 1.25rem auto 0;
  }

  .risk-gauge-dial {
    position: relative;
    width: 240px;
    max-width: 100%;
    height: 130px;
    margin: 0 auto;
    overflow: hidden;
  }

  .risk-gauge-arc {
    position: absolute;
    top: 0;
    left: 0;
    width: 240px;
    height: 240px;
    border-radius: 50%;
    background: conic-gradient(
      from 270deg,
      #ef4444 0deg 90deg,
      #f97316 90deg 126deg,
      #f59e0b 126deg 135deg,
      #facc15 135deg 144deg,
      #84cc16 144deg 162deg,
      #16a34a 162deg 180deg,
      transparent 180deg 360deg
    );
  }

  .risk-gauge-mask {
    position: absolute;
    z-index: 2;
    top: 42px;
    left: 42px;
    width: 156px;
    height: 156px;
    border-radius: 50%;
    background: #ffffff;
  }

  .risk-gauge-needle {
    position: absolute;
    z-index: 3;
    left: 50%;
    top: 118px;
    width: 82px;
    height: 3px;
    border-radius: 999px;
    background: #1e3a5f;
    transform-origin: 0 50%;
    transition: transform 0.5s ease;
  }

  .risk-gauge-hub {
    position: absolute;
    z-index: 4;
    left: calc(50% - 9px);
    top: 110px;
    width: 18px;
    height: 18px;
    border: 5px solid #1e3a5f;
    border-radius: 50%;
    background: #ffffff;
    box-sizing: border-box;
  }

  .risk-gauge-scale {
    display: flex;
    justify-content: space-between;
    margin-top: -0.1rem;
    color: #94a3b8;
    font-family: $brand-font-medium;
    font-size: 0.65rem;
    font-variant-numeric: tabular-nums;
  }

  .risk-gauge-legend {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.4rem 0.75rem;
    margin-top: 0.75rem;
    color: #475569;
    font-family: $brand-font-regular;
    font-size: 0.64rem;
    line-height: 1.25;

    span {
      display: flex;
      align-items: center;
      gap: 0.35rem;
      min-width: 0;
    }
  }

  .gauge-key {
    display: inline-block;
    width: 8px;
    height: 8px;
    flex-shrink: 0;
    border-radius: 2px;

    &-unacceptable { background: #ef4444; }
    &-high { background: #f97316; }
    &-moderate { background: #f59e0b; }
    &-moderately-low { background: #facc15; }
    &-low { background: #84cc16; }
    &-very-low { background: #16a34a; }
  }

  .summary-final-main {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 0.75rem;
    margin-top: 1rem;
  }

  .summary-final-score {
    position: relative;
    top: 7px;
    font-family: $brand-font-medium;
    font-variant-numeric: tabular-nums;

    strong {
      color: $color-primary;
      font-size: 2rem;
      font-weight: 700;
      letter-spacing: -0.5px;

      &.risk-rating-very-low { color: #16a34a; }
      &.risk-rating-low { color: #65a30d; }
      &.risk-rating-moderately-low { color: #ca8a04; }
      &.risk-rating-moderate { color: #d97706; }
      &.risk-rating-high { color: #ea580c; }
      &.risk-rating-unacceptable { color: #ef4444; }
      &.risk-rating-unrated { color: #64748b; }
    }

    span {
      color: #64748b;
      font-size: 0.95rem;
      font-weight: 500;
    }
  }

  .risk-rating-badge {
    display: inline-block;
    padding: 0.35rem 0.6rem;
    border-radius: 0;
    color: #ffffff;
    font-family: $brand-font-medium;
    font-size: 0.9rem;
    font-weight: 500;
    text-transform: uppercase;
    white-space: nowrap;

    &.risk-rating-very-low { background: #16a34a; }
    &.risk-rating-low { background: #65a30d; }
    &.risk-rating-moderately-low { background: #ca8a04; }
    &.risk-rating-moderate { background: #d97706; }
    &.risk-rating-high { background: #ea580c; }
    &.risk-rating-unacceptable { background: #ef4444; }
    &.risk-rating-unrated { background: #64748b; }
  }
}

.risk-sections-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem 2rem;
  align-items: start;
  min-width: 0;
}

.approved-payers-section {
  padding: 1.5rem 0;
  margin: 0;

  .section-title {
    font-size: 1.125rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 1rem 0;
    font-family: $brand-font-medium;
    letter-spacing: -0.2px;
  }
}

.approved-payers-list {
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.approved-payers-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eef2f7;

  &:last-child {
    border-bottom: none;
  }
}

.approved-payers-index {
  flex-shrink: 0;
  width: 1.75rem;
  color: #64748b;
  font-family: $brand-font-medium;
  font-size: 0.875rem;
  font-variant-numeric: tabular-nums;
}

.approved-payers-name {
  color: #1e293b;
  font-family: $brand-font-regular;
  font-size: 0.95rem;
  line-height: 1.4;
}

.approved-payers-empty {
  margin: 0;
  color: #64748b;
  font-family: $brand-font-regular;
  font-size: 0.95rem;
}

.risk-section {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 1.5rem 0;
  margin: 0;
  box-shadow: none;
  min-width: 0;
  
  .section-title {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    font-size: 1.125rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 1.25rem 0;
    font-family: $brand-font-medium;
    letter-spacing: -0.2px;
    
    i {
      color: $color-primary;
      font-size: 1.125rem;
      background: transparent;
      padding: 0;
      border-radius: 0;
      width: auto;
      height: auto;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  
  .metrics-table-container {
    overflow-x: hidden;
    max-width: 100%;
    border-radius: 0;
    border: none;
  }
  
  .metrics-table {
    width: 100%;
    max-width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    background: transparent;
    
    thead {
      background: transparent;
      
      th {
        padding: 0.5rem 1rem;
        text-align: left;
        font-weight: 700;
        color: #0f172a;
        font-family: $brand-font-medium;
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        border: none;

        &:nth-child(1) {
          width: 45%;
        }

        &:nth-child(2) {
          width: 35%;
        }

        &:nth-child(3) {
          width: 20%;
          text-align: center;
        }
      }
    }
    
    tbody {
      tr {
        border: none;
        background: transparent;
        
        &:hover {
          background: transparent;
        }
        
        &.risk-total-row {
          background: transparent;
          font-weight: 700;
          border: none;
          
          td {
            font-family: $brand-font-medium;
            color: #0f172a;
            padding: 0.5rem 1rem;
            font-size: 0.9rem;
          }
        }
        
        &:last-child {
          border-bottom: none;
        }
      }
      
      td {
        padding: 0.375rem 1rem;
        color: #475569;
        font-family: $brand-font-regular;
        font-size: 0.875rem;
        vertical-align: middle;
        border: none;
        background: transparent;
        
        &.metric-name {
          font-weight: 600;
          color: #1e293b;
          font-size: 0.875rem;
          word-break: break-word;
        }
        
        &.current-value {
          color: #475569;
          font-weight: 500;
          font-family: $brand-font-medium;
          word-break: break-word;
        }
        
        &.obtained-score-cell {
          width: 20%;
          text-align: center;
          
          .score-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.3rem;
            
            .obtained-score {
              font-weight: 700;
              font-size: 0.95rem;
              font-family: $brand-font-medium;
              
              &.score-excellent {
                color: #059669;
              }
              
              &.score-good {
                color: #ca8a04;
              }
              
              &.score-fair {
                color: #d97706;
              }
              
              &.score-poor {
                color: #dc2626;
              }
            }

            .score-ratio {
              font-size: 0.8rem;
              font-variant-numeric: tabular-nums;
            }
            
            .score-progress-mini {
              width: 100%;
              max-width: 72px;
              height: 6px;
              background: #e2e8f0;
              border-radius: 999px;
              overflow: hidden;
              
              .progress-mini-fill {
                height: 100%;
                border-radius: inherit;
                transition: width 0.35s ease;
                
                &.score-excellent {
                  background: #10b981;
                }
                
                &.score-good {
                  background: #eab308 ;
                }
                
                &.score-fair {
                  background: #f59e0b;
                }
                
                &.score-poor {
                  background: #ef4444;
                }
              }
            }
          }
        }
      }
    }
  }
}

.risk-section {
  .metrics-table tbody tr:not(:last-child),
  .metrics-table tbody tr.risk-total-row:not(:last-child) {
    td {
      font-weight: 400;
      font-family: $brand-font-regular;

      &.metric-name {
        font-weight: 400;
      }

      &.current-value {
        font-weight: 400;
        font-family: $brand-font-regular;
      }

      &.obtained-score-cell .obtained-score {
        font-weight: 400;
        font-family: $brand-font-regular;
      }
    }
  }

  .metrics-table tbody tr:last-child {
    td {
      font-weight: 700;
      font-family: $brand-font-medium;

      &.metric-name {
        font-weight: 700;
      }

      &.current-value {
        font-weight: 600;
        font-family: $brand-font-medium;
      }

      &.obtained-score-cell .obtained-score {
        font-weight: 700;
        font-family: $brand-font-medium;
      }
    }
  }
}

.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  background: transparent;
  border-radius: 0;
  border: none;
  
  .no-data-icon {
    font-size: 3rem;
    color: #adb5bd;
    margin-bottom: 1rem;
  }
  
  h3 {
    color: #6c757d;
    margin: 0 0 0.5rem 0;
    font-size: 1.2rem;
    font-family: $brand-font-medium;
  }
  
  p {
    color: #adb5bd;
    margin: 0;
    font-family: $brand-font-regular;
  }
}

@media (max-width: 768px) {
  .assessment-header {
    padding: 2rem 1.5rem;
    
    .header-content {
      flex-direction: column;
      align-items: flex-start;
      text-align: left;
      gap: 1rem;
      
      .header-icon {
        width: 56px;
        height: 56px;
        
        i {
          font-size: 1.75rem;
        }
      }
      
      .header-text {
        .assessment-title {
          font-size: 1.5rem;
        }
        
        .assessment-subtitle {
          font-size: 0.9rem;
        }
      }
    }
  }
  
  .summary-section {
    padding: 1.5rem 0;

    .section-title {
      font-size: 1.125rem;
    }

    .summary-layout {
      grid-template-columns: 1fr;
    }

    .summary-category-header,
    .summary-category-row {
      grid-template-columns: minmax(0, 1fr) 100px;
      gap: 0.5rem;
      padding-left: 0.75rem;
      padding-right: 0.75rem;
    }

    .summary-final-card {
      min-height: 0;
    }
  }
  
  .risk-sections-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .risk-section {
    padding: 1.5rem;
    
    .section-title {
      font-size: 1.25rem;
      margin-bottom: 1.5rem;
    }
    
    .metrics-table-container {
      font-size: 0.85rem;
    }
    
    .metrics-table {
      thead th,
      tbody td {
        padding: 0.375rem 0.5rem;
      }
      
      tbody td.obtained-score-cell .score-container {
        .obtained-score {
          font-size: 1rem;
        }
        
        .score-progress-mini {
          width: 100%;
          max-width: 72px;
        }
      }
    }
  }
}
</style>

